import { useState } from 'react'
import { Head, Link, router, usePage } from '@inertiajs/react'
import ImpersonationBanner from '../../components/layout/ImpersonationBanner'

interface CompanyRequest {
  id: number
  proposed_org_name: string
  status: string
  requested_at_display: string
  provisioned_organization_id: number | null
}

interface Props {
  errors: Record<string, string | string[]>
  requests: CompanyRequest[]
  has_return_destination: boolean
}

const STATUS_STYLES: Record<string, string> = {
  pending: 'bg-[#B45309]/10 text-[#B45309]',
  approved: 'bg-[#15532E]/10 text-[#15532E]',
  denied: 'bg-[#ED1C24]/10 text-[#ED1C24]',
}

export default function NewCompany() {
  const { errors, requests, has_return_destination, csrf_token } = usePage().props as unknown as Props & { csrf_token?: string }
  const [name, setName] = useState('')
  const [notes, setNotes] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const err = (k: string): string | null => {
    const v = errors?.[k]
    if (!v) return null
    return Array.isArray(v) ? v[0] : v
  }

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitting(true)
    router.post('/companies', {
      company: { name, notes },
    }, {
      onFinish: () => setSubmitting(false),
      onSuccess: () => { setName(''); setNotes('') },
    })
  }

  return (
    <>
      <Head title="Create a company - Pinnacle" />
      <ImpersonationBanner />
      <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center p-6">
        <div className="max-w-lg w-full">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-text-primary">Pinnacle</h1>
            <p className="mt-2 text-text-secondary">Request a new company workspace</p>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-8">
            <p className="text-sm text-text-muted mb-5">
              As a Guide, you can request a new company. A Pinnacle administrator
              reviews each request — once approved, you&rsquo;ll get temporary
              admin access to set it up and then hand it off to the company&rsquo;s
              owner.
            </p>

            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-text-primary mb-1.5">Company name</label>
                <input
                  value={name}
                  onChange={e => setName(e.target.value)}
                  required
                  className="w-full border border-stone/30 rounded-lg px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-charcoal/40 focus:border-charcoal"
                  placeholder="Acme Inc."
                />
                {err('name') && <p className="text-xs text-[#ED1C24] mt-1">{err('name')}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-text-primary mb-1.5">Notes (optional)</label>
                <textarea
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  rows={3}
                  className="w-full border border-stone/30 rounded-lg px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-charcoal/40 focus:border-charcoal resize-none"
                  placeholder="Anything the reviewer should know..."
                />
              </div>

              <button
                type="submit"
                disabled={submitting || !name.trim()}
                className="w-full px-5 py-2.5 bg-pinnacle-red text-white text-sm font-semibold rounded-lg hover:bg-pinnacle-red/90 disabled:opacity-50"
              >
                {submitting ? 'Submitting...' : 'Submit request'}
              </button>
            </form>
          </div>

          {requests.length > 0 && (
            <div className="bg-white rounded-xl shadow-lg p-6 mt-4">
              <h2 className="text-sm font-semibold text-text-primary mb-3">Your requests</h2>
              <div className="space-y-2">
                {requests.map(r => (
                  <div key={r.id} className="flex items-center justify-between text-sm">
                    <div>
                      <span className="text-text-primary font-medium">{r.proposed_org_name}</span>
                      <span className="text-text-muted ml-2">{r.requested_at_display}</span>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full capitalize ${STATUS_STYLES[r.status] || 'bg-stone/20 text-text-muted'}`}>
                      {r.status}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="text-center mt-6 flex items-center justify-center gap-4">
            {has_return_destination && (
              <>
                <Link href="/" className="text-sm text-text-muted hover:text-text-primary">
                  Back to app
                </Link>
                <span className="text-text-muted">·</span>
              </>
            )}
            <button
              type="button"
              onClick={() => router.delete('/users/sign_out', { data: { authenticity_token: csrf_token ?? '' } })}
              className="text-sm text-text-muted hover:text-text-primary"
            >
              Sign out
            </button>
          </div>
        </div>
      </div>
    </>
  )
}
